import React, { useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { InvoiceStatsData } from '../../@types';
import InvoicesTable from '../../components/invoice/Tables/Table';
import { getInvoiceStats } from '../../backend-services';
import { invoiceService, configureInvoiceService } from '../../services';
import { InfoCard, InfoCardProps } from '../../components/core/InfoCard';
import { ActiveIcon, NariaIcon, NumberIcon, ProfileIcon } from '../../assets/icons';
import { useInvoiceStore } from '../../state';
import shallow from 'zustand/shallow';
import { useInvoiceSearch } from '../../hooks';

configureInvoiceService(invoiceService);

const ListInvoicePage = () => {
  const location = useLocation();
  const [params] = useSearchParams();

  const [data, setData] = React.useState<InvoiceStatsData>({
    totalInvoices: 0,
    totalPaidInvoices: 0,
    totalPendingInvoices: 0,
    totalOverDueInvoices: 0,
    totalCancelledInvoices: 0,
    totalDraftInvoice: 0,
    totalInvoiceValue: 0,
    totalRefundedInvoices: 0,
    dateRange: '',
  });

  const fetchGetInvoiceStats = async () => {
    const currentDate = new Date();
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString();
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).toISOString();
    const response = await getInvoiceStats(startDate, endDate);

    setData({
      totalInvoices: response.totalInvoices,
      totalPaidInvoices: response.totalPaidInvoices,
      totalPendingInvoices: response.totalPendingInvoices,
      totalOverDueInvoices: response.totalOverDueInvoices,
      totalCancelledInvoices: response.totalCancelledInvoices,
      totalDraftInvoice: response.totalDraftInvoice,
      totalInvoiceValue: response.totalInvoiceValue,
      totalRefundedInvoices: response.totalRefundedInvoices,
      dateRange: `${currentDate.toDateString()} - ${new Date(endDate).toDateString()}`,
    });
  };

  const { data: searchResult, isSearching, handleSearch: handleInvoiceSearch } = useInvoiceSearch();

  const { fetchInvoices, fetchMoreData, onSearchData, handleFilter, fetchData, handleSort } = useInvoiceStore(
    (state) => ({
      fetchInvoices: state.fetchInvoices,
      fetchMoreData: state.fetchMoreData,
      onSearchData: state.setSearchText,
      handleFilter: state.setFilter,
      fetchData: state.fetchData,
      handleSort: state.handleSort,
      setFilter: state.setFilter,
    }),
    shallow,
  );

  const { invoices, isLoading, range, pagination, searchText, filter } = useInvoiceStore(
    (state) => ({
      invoices: state.invoices,
      isLoading: state.isLoading,
      filter: state.filter,
      searchText: state.searchText,
      range: state.range,
      pagination: state.pagination,
      error: state.error,
    }),
    shallow,
  );

  useEffect(() => {
    fetchGetInvoiceStats();
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [searchText, filter, range, fetchData]);

  useEffect(() => {
    fetchInvoices();
  }, [params, location.state, fetchInvoices]);

  const infoCardData = [
    {
      title: 'Total Invoices',
      value: `${data.totalInvoices}`,
      color: 'secondary',
      icon: <NariaIcon />,
      isLoading: isLoading,
    },
    {
      title: 'Paid',
      value: `${data.totalPaidInvoices}`,
      color: 'active',
      icon: <NumberIcon />,
      isLoading: isLoading,
    },
    {
      title: 'Pending',
      value: `${data.totalPendingInvoices}`,
      color: 'primary',
      icon: <ProfileIcon />,
      isLoading: isLoading,
    },
    {
      title: 'Overdue',
      value: `${data.totalOverDueInvoices}`,
      color: 'danger',
      icon: <ActiveIcon />,
      isLoading: isLoading,
    },
  ] as InfoCardProps[];

  console.log(invoices);
  return (
    <>
      <div className="flex flex-col  ">
        <div className="flex justify-between items-center">
          <h1 className="font-bold text-[18px] 540:ml-[3em] 412:ml-[-1em] 414:ml-[-1em] sm:ml-[5em] ml-[-1.6em] bigmd:ml-[2.2em] xl:text-[20px] lg:ml-[0.3em] md:ml-[2.8em] xl:ml-[0.1em] 360:ml-[-2.1em] ">
            Invoices
          </h1>
          <p className="text-[12px] 2xl:text-[14px] 540:mr-[3em]  md:mr-[2.8em] bigmd:mr-[2.2em] 375:mr-[-3.2em] 414:mr-[-2.2em] 360:mr-[-3.3em] 412:mr-[-1.2em] mr-[-2.7em]">
            Last 30 days
          </p>
        </div>

        <div className="hidden lg:block mt-4">
          <div className="grid grid-cols-4 gap-2 xl:ml-[0.3em] lg:ml-[0.3em] ">
            {infoCardData.map((cardData, index) => (
              <div className="" key={index}>
                <InfoCard {...cardData} />
              </div>
            ))}
            {infoCardData.length < 4 &&
              Array.from({ length: 4 - infoCardData.length }).map((_, index) => <div key={index}></div>)}
          </div>
        </div>
        <div className=" lg:hidden mt-4 ">
          <div className="flex justify-center md:ml-[0.5em] bigmd:w-[] gap-5 sm:gap-8 sm:ml-[-0.3em] ml-[1.5em] 375:ml-[0.2em] 414:ml-[0.3em] 360:ml-[0.2em] 412:ml-[-0.1em] smmd:">
            {infoCardData.slice(0, 2).map((cardData, index) => (
              <div key={index}>
                <InfoCard {...cardData} />
              </div>
            ))}
          </div>
          <div className="mt-6 md:mt-2"></div>
          <div className="flex justify-center md:ml-[0.5em] gap-5 sm:gap-8 sm:ml-[-0.3em] ml-[-2em] 375:ml-[0.2em]  414:ml-[0.3em] 360:ml-[0.2em] 412:ml-[-0.1em]">
            {infoCardData.slice(2, 4).map((cardData, index) => (
              <div key={index}>
                <InfoCard {...cardData} />
              </div>
            ))}
          </div>
        </div>

        <div className="hidden md:block">
          <InvoicesTable
            fetchMoreData={fetchMoreData}
            onSearchData={onSearchData}
            handleSort={handleSort}
            handleFilter={handleFilter}
            handleSearch={handleInvoiceSearch}
            meta={pagination}
            invoices={invoices}
            isLoading={isLoading}
            isSearching={isSearching}
            searchResult={searchResult}
            selectedFilter={filter}
          />
        </div>

        <div className="flex md:hidden justify-center items-center">
          <InvoicesTable
            fetchMoreData={fetchMoreData}
            onSearchData={onSearchData}
            handleSort={handleSort}
            handleFilter={handleFilter}
            handleSearch={handleInvoiceSearch}
            meta={pagination}
            invoices={invoices}
            isLoading={isLoading}
            isSearching={isSearching}
            searchResult={searchResult}
            selectedFilter={filter}
          />
        </div>
      </div>
    </>
  );
};

export { ListInvoicePage };
